/**
 * IAM bounded-context widget registry.
 *
 * Each widget is registered via the typed factory (`defineStatWidget` /
 * `defineChartWidget`) and exposed as `provideWidget(...)` entries.
 * Spread `iamWidgetProviders` in the IAM providers array — same pattern
 * as `iamIcons` in `nav-main.ts`.
 *
 * Widgets declared here:
 *   - `iam.accounts.stat`  — stat-tile: total active accounts (RefreshableWidget)
 *   - `iam.accounts.chart` — pie chart: account distribution by type
 *
 * Icons are SVGs served from a CDN (Iconify) via `iconUrl`, NOT `@ng-icons` imports —
 * the catalog renders them with an `<img>`, so a BC adds widgets without registering
 * icon symbols. A `?color=` keeps each glyph visible on both light and dark surfaces.
 *
 * Permission gate: both widgets use `iam.account.get` — the exact @Auth string
 * declared on IamPaginateAccountsResolver, IamGetAccountsResolver, and
 * IamCountAccountResolver. This ensures that widget visibility always coincides
 * with data-access permission, so an account can never see a widget whose
 * underlying query would return 403.
 */
import type { ChartSeries } from '@aurora/modules/widget';
import {
  defineChartWidget,
  defineStatWidget,
  provideWidget,
  WIDGET_CATEGORY,
} from '@aurora/modules/widget';
import gql from 'graphql-tag';

// ── Stat data source ──────────────────────────────────────────────────────────

/**
 * Dedicated count query — uses the explicit `iamCountAccount` resolver (Int scalar).
 * Replaces the old `paginate(limit:0)` stopgap; no rows are fetched.
 * Filtered by `isActive: true` so the tile counts ACTIVE accounts only (matches the
 * "Active accounts" label) — isActive is distinct from soft-delete (deletedAt).
 */
const accountCountQuery = gql`
  query IamCountAccounts($query: QueryStatementInput, $constraint: QueryStatementInput) {
    count: iamCountAccount(query: $query, constraint: $constraint)
  }
`;

/**
 * Maps the raw iamCountAccount response to a plain number.
 * The resolver returns a nullable scalar Int aliased as `count`.
 */
function mapCountToTotal(raw: unknown): number {
  return (raw as { count?: number | null }).count ?? 0;
}

// ── Chart data source ─────────────────────────────────────────────────────────

/**
 * Lightweight query: fetch only `id` and `type` for all accounts so we can
 * group SERVICE vs USER client-side without a server-side group-by endpoint.
 * Uses `limit: 10_000` — accounts is a bounded-size admin entity.
 */
const accountTypesQuery = gql`
  query IamGetAccountTypes($query: QueryStatementInput, $constraint: QueryStatementInput) {
    objects: iamGetAccounts(query: $query, constraint: $constraint) {
      id
      type
    }
  }
`;

interface AccountTypeItem {
  id: string;
  type: string;
}

/**
 * Maps the raw iamGetAccounts response to a `ChartSeries[]` pie slice.
 *
 * Counts occurrences of each `type` value and produces one named data-point
 * per type. The stable `localeCompare` sort ensures each type always occupies
 * the same palette slot across refreshes (ECharts assigns colors by position).
 */
function mapAccountsToPieSeries(raw: unknown): ChartSeries[] {
  const data = raw as { objects?: AccountTypeItem[] };
  const accounts = data?.objects ?? [];

  const counts = accounts.reduce<Record<string, number>>((acc, account) => {
    const key = account.type ?? 'UNKNOWN';
    acc[key] = (acc[key] ?? 0) + 1;
    return acc;
  }, {});

  const sortedData = Object.entries(counts)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, value]) => ({ name, value }));

  return [{ data: sortedData }];
}

// ── Widget definitions ────────────────────────────────────────────────────────

export const iamWidgetProviders = [
  provideWidget(
    defineStatWidget({
      id: 'iam.accounts.stat',
      translation: 'dashboard.widgets.iam.ActiveAccountsStat',
      description: 'dashboard.widgets.iam.ActiveAccountsStatDescription',
      iconUrl: 'https://api.iconify.design/lucide/users.svg?color=%236366f1',
      permission: 'iam.account.get',
      category: WIDGET_CATEGORY.KPIS,
      span: {
        minCols: 2,
        maxCols: 6,
        minRows: 1,
        maxRows: 2,
        defaultCols: 3,
        defaultRows: 1,
      },
      format: 'compact',
      source: {
        kind: 'graphql',
        document: accountCountQuery,
        variables: { query: { where: { isActive: { eq: true } } } },
        map: mapCountToTotal,
        fetchPolicy: 'network-only',
      },
    }),
  ),
  provideWidget(
    defineChartWidget({
      id: 'iam.accounts.chart',
      translation: 'dashboard.widgets.iam.AccountsByTypeChart',
      description: 'dashboard.widgets.iam.AccountsByTypeChartDescription',
      iconUrl: 'https://api.iconify.design/lucide/chart-pie.svg?color=%238b5cf6',
      permission: 'iam.account.get',
      category: WIDGET_CATEGORY.CHARTS,
      span: {
        minCols: 4,
        maxCols: 12,
        minRows: 4,
        maxRows: 8,
        defaultCols: 6,
        defaultRows: 5,
      },
      chartType: 'pie',
      i18nLabels: true,
      i18nLabelKeyPrefix: 'iam.account.Type.',
      source: {
        kind: 'graphql',
        document: accountTypesQuery,
        variables: { query: { offset: 0, limit: 10_000 } },
        map: mapAccountsToPieSeries,
        fetchPolicy: 'network-only',
      },
    }),
  ),
];
